import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  recurring: "none" | "monthly" | "yearly";
  incurred_on: string;
  client_project_id: string | null;
  notes: string | null;
  created_at: string;
}

export function useExpenses() {
  return useQuery({
    queryKey: ["expenses"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("expenses" as never)
        .select("*")
        .order("incurred_on", { ascending: false });
      if (error) throw error;
      return data as Expense[];
    },
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Omit<Expense, "id" | "created_at">) => {
      const { error } = await supabase.from("expenses" as never).insert(data as never);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["finance-stats"] });
      toast.success("Despesa registrada");
    },
    onError: (e: Error) => toast.error("Erro: " + e.message),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("expenses" as never)
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["finance-stats"] });
      toast.success("Despesa removida");
    },
    onError: () => toast.error("Não foi possível remover a despesa"),
  });
}
